import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, from, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AppInterceptor implements HttpInterceptor {
  requestsInProcess = 0;
  constructor(private spinner: NgxSpinnerService, private authService: MsalService) { }

  checkTokenPreexpiration(tokenClaims: any) {
    const minutesLeftToExpire = (tokenClaims.exp * 1000 - new Date().getTime()) / 1000 / 60;
    if (minutesLeftToExpire < 10) {
      console.log("Token expiring in less than 10 minutes. Forcing refresh");
      return this.refreshToken(true);
    }

    return Promise.resolve();
  }

  refreshToken(forceRefresh?: boolean): Promise<void> {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return new Promise((resolve, reject) => {
        this.authService.acquireTokenSilent({
          scopes: ["User.Read"],
          account: activeAccount,
          forceRefresh
        }).subscribe({
          next: (result: AuthenticationResult) => {
            if (!forceRefresh) {
              this.checkTokenPreexpiration(jwtDecode(result.accessToken)).then(resolve);
            } else {
              resolve();
            }
          },
          error: (error) => {
            // If silent token acquisition fails, fallback to interactive
            if (error instanceof InteractionRequiredAuthError) {
              this.authService.acquireTokenPopup({
                scopes: ["User.Read"],
              }).subscribe({
                next: (result: AuthenticationResult) => {
                  resolve();
                },
                error: (error) => {
                  console.log(error);
                  reject(error);
                }
              });
            } else {
              console.log(error);
              reject(error);
            }
          }
        });
      });
    } else {
      console.log("No active account! Please log in.");
      return Promise.resolve();
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let noSpinnerFlag = false;
    if (req.headers.has(environment.noLoadingSpinnerHeader)) {
      noSpinnerFlag = true;
      // HttpHeaders are immutable: clone so the custom header is not sent to the backend (CORS)
      req = req.clone({ headers: req.headers.delete(environment.noLoadingSpinnerHeader) });
    } else {
      this.requestsInProcess++;
      setTimeout(() => {
        this.spinner.show();
      }, 0);
    }

    return from(environment.authEnabled ? this.refreshToken() : Promise.resolve()).pipe(
      mergeMap(() => {
        // This is used for internationalization. For any request is setting the language in Accept-Language request header
        // This will be read on the Backend side to set the Backend locale language
        req = req.clone({
          headers: req.headers.set('Accept-Language', 'en'),
        });

        return next.handle(req).pipe(
          map((event: HttpEvent<any>) => {
            return event;
          }),
          catchError((error: HttpErrorResponse) => {
            const started = Date.now();
            const elapsed = Date.now() - started;
            // Background requests (no spinner, e.g. photo thumbnails) handle their own errors quietly
            if (!noSpinnerFlag) {
              console.log(
                `Request for ${req.urlWithParams} failed after ${elapsed} ms.`
              );
            }
            return throwError(() => error);
          })
        );
      }),
      finalize(() => {
        if (!noSpinnerFlag) {
          this.requestsInProcess = Math.max(0, --this.requestsInProcess);
          if (this.requestsInProcess === 0) {
            this.spinner.hide();
          }
        }
      })
    );
  }
}
